/**
 * ⭐ `R4-UIM1` · **LO QUE LAS DOS TABLAS DICEN: GRUPOS, COLUMNAS, DICCIONARIO Y
 * LA LISTA FIJA DE ERRORES.**
 *
 * Vive aparte de `pantalla.tsx` por una razón de lectura y no de gusto: son
 * **cuatro listas textuales del PRD** —catorce columnas, tres grupos, diecisiete
 * entradas de diccionario y seis columnas de error— y un `.tsx` con el JSX de
 * dos tablas, dos pestañas y tres popups ADEMÁS de 170 líneas de copy deja de
 * poder leerse. Ninguna de estas listas tiene lógica: son datos.
 *
 * ⛔⛔ **TODOS LOS TEXTOS SON TEXTUALES DEL PRD, Y ÉSA ES SU ÚNICA FUENTE.**
 *   · Grupos y columnas · `PRD_R4.md:1639-1648` (§Columnas).
 *   · Diccionario · `PRD_R4.md:1686-1705` (§Qué significa cada métrica).
 *   · Nota del brazo · `PRD_R4.md:1706`.
 *   · Lista de errores · `PRD_R4.md:1716-1724` (§Pestaña «Errores de Meta»).
 *   · Nota de los errores · `PRD_R4.md:1726`.
 *   · Leyendas · `PRD_R4.md:1637` y `:1712`.
 *
 * Los criterios `R4-F3-CA-10`, `CA-40`, `CA-45`, `CA-46` y `CA-43` los citan
 * entre comillas, así que **una coma de diferencia es un rojo**: no se parafrasea
 * ni se «mejora» nada acá.
 *
 * ⛔ **Lo que este archivo NO tiene:** ni un «KAI» literal. El sello del brazo lo
 * arma `selloDelBrazo()` de `zonas.ts` a partir del nombre de la instancia y del
 * brazo (`R4-G0`, gate de `F4-CA-15`), y por eso las leyendas son FUNCIONES del
 * sello y no cadenas.
 */

import type { MetricasDePeriodo } from "./formato-de-metrica.ts"
import {
  SIN_DATO,
  textoDeDelta,
  textoDeEntero,
  textoDeMetricValue,
  textoDePromedio,
} from "./formato-de-metrica.ts"

// ════════════════════════════════════════════════════════════════════════════
// LOS TRES GRUPOS DEL ENCABEZADO DE DOS NIVELES
// ════════════════════════════════════════════════════════════════════════════

/**
 * `PRD_R4.md:1639`: *«El encabezado tiene dos niveles. Arriba, sobre las
 * columnas que agrupan: «Mensajes inductores», «Personas» e «Intensidad».»*
 *
 * ⚠️ Son los rótulos del nivel de ARRIBA y nada más. Item, Desde – Hasta y
 * Acciones **no pertenecen a ningún grupo** (`PRD_R4.md:1642-1648`, columna
 * «Grupo» con «—»): en el nivel de arriba les corresponde una celda vacía que
 * abarca las dos filas, no un grupo inventado.
 */
export const GRUPO_MENSAJES = "Mensajes inductores"
export const GRUPO_PERSONAS = "Personas"
export const GRUPO_INTENSIDAD = "Intensidad"

/** Los grupos en el orden del PRD, para el `<tr>` de arriba. */
export type Grupo = typeof GRUPO_MENSAJES | typeof GRUPO_PERSONAS | typeof GRUPO_INTENSIDAD

// ════════════════════════════════════════════════════════════════════════════
// LAS CATORCE COLUMNAS DE MÉTRICA
// ════════════════════════════════════════════════════════════════════════════

/**
 * El título de la única columna que no es de los dos brazos.
 *
 * ⛔ Constante y no un literal repetido: el filtro por capacidad la busca por
 * título y la entrada del diccionario la nombra. Dos cadenas iguales escritas en
 * dos lugares es la forma de que un retoque de copy apague el filtro sin poner
 * rojo a nada. ⟦se conserva de `CP-F4-010`⟧
 */
export const TITULO_PIDIO_HUMANO = "Pidió humano"

/**
 * Una columna de métrica: su título, su grupo y cómo se dibuja su celda.
 *
 * ⛔ `render` NO calcula: llama a la función de formato de
 * `formato-de-metrica.ts` (B15) sobre el campo que el borde ya trae calculado.
 * Ni una división, ni un `Math.round`, ni un `%` recalculado (`[no-va]`).
 */
export interface ColumnaDeMetrica {
  readonly titulo: string
  readonly grupo: Grupo
  readonly render: (metrics: MetricasDePeriodo) => string
}

/**
 * **LAS CATORCE, EN EL ORDEN DEL PRD** (`PRD_R4.md:1642-1648`).
 *
 * ⭐ `R4-CAN1` las subió de diez a catorce: las cuatro de «Intensidad»
 * (`promedioInteraccionesPorPersona`, `sesiones`, `sesionesPorPersona`,
 * `mensajesDeLasPersonas`) entraron con `R4-F3` RT 3.
 *
 * ⟦rastro · ADR-0082 · 05/10/2026 · R4-UIM1⟧ · **LOS RÓTULOS VIEJOS, QUE NO SE
 * BORRAN.** Hasta hoy esta lista decía «Usuarios con interacción», «Usuarios con
 * 2+ interacciones» e «Interacción vs. periodo anterior», y cada entrada llevaba
 * además una `definicion` que se dibujaba en un globo sobre un ícono de info.
 * `PRD_R4.md:1644` fija los rótulos nuevos —«Interacción positiva», «2+
 * interacciones positivas», «Interacción positiva vs. periodo anterior»— y
 * `PRD_R4.md:1686` mueve las definiciones a «Qué significa cada métrica», debajo
 * del paginado, con otros textos. Ver el rastro del ícono de info en
 * `pantalla.tsx` §⟦el-icono-de-info-se-retira⟧.
 */
export const COLUMNAS: readonly ColumnaDeMetrica[] = [
  // ── Mensajes inductores ───────────────────────────────────────────────────
  {
    titulo: "Enviados",
    grupo: GRUPO_MENSAJES,
    // `PRD_R4.md:1734`: «El número, sin porcentaje («11»)».
    render: (m) => textoDeEntero(m.enviados),
  },
  { titulo: "Entregados", grupo: GRUPO_MENSAJES, render: (m) => textoDeMetricValue(m.entregados) },
  { titulo: "Leídos", grupo: GRUPO_MENSAJES, render: (m) => textoDeMetricValue(m.leidos) },

  // ── Personas ──────────────────────────────────────────────────────────────
  {
    titulo: "Interacción positiva",
    grupo: GRUPO_PERSONAS,
    render: (m) => textoDeMetricValue(m.usuariosConInteraccion),
  },
  {
    titulo: "2+ interacciones positivas",
    grupo: GRUPO_PERSONAS,
    render: (m) => textoDeMetricValue(m.usuariosConDosInteracciones),
  },
  {
    titulo: "Interacción positiva vs. periodo anterior",
    grupo: GRUPO_PERSONAS,
    render: (m) => textoDeDelta(m.interaccionVsPeriodoAnterior),
  },
  {
    titulo: "Sin interacción",
    grupo: GRUPO_PERSONAS,
    render: (m) => textoDeMetricValue(m.sinInteraccion),
  },
  {
    titulo: "Siguió con el chatbot",
    grupo: GRUPO_PERSONAS,
    render: (m) => textoDeMetricValue(m.siguioConChatbot),
  },
  {
    titulo: TITULO_PIDIO_HUMANO,
    grupo: GRUPO_PERSONAS,
    /*
     * ⛔⛔ **EL ÚNICO `render` QUE TIENE QUE ESTRECHAR, Y EL CONTRATO LO OBLIGA.**
     *
     * `PeriodMetricRow.metrics` es la unión `PeriodMetrics |
     * PeriodMetricsSinDerivacion` (`contracts/analytics/index.ts:1303`), y en la
     * variante sin derivación `pidioHumano` es `?: never`. Leer
     * `m.pidioHumano` sobre la unión da `MetricValue | undefined`, y el propio
     * contrato lo deja escrito: *«hay que estrechar antes de usarlo, y eso es
     * deliberado»*.
     *
     * ⛔ **Y el `undefined` NO se tapa con un valor inventado.** `ADR-0304`-la-guarda:
     * ni un `?? 0`, ni un `{ count: 0, … }`. Si el campo no vino, esta columna no
     * se dibuja —el filtro por capacidad la saca de la lista entera, encabezado
     * incluido— y este `render` no llega a correr. El `SIN_DATO` de abajo es la
     * red por si alguien la dibujara igual: una celda «—» se ve y se reporta; un
     * «0 · 0 %» inventado se lee como un dato y nadie lo reporta.
     */
    render: (m) => (m.pidioHumano === undefined ? SIN_DATO : textoDeMetricValue(m.pidioHumano)),
  },
  { titulo: "Opt-out", grupo: GRUPO_PERSONAS, render: (m) => textoDeMetricValue(m.optOut) },

  // ── Intensidad ────────────────────────────────────────────────────────────
  {
    titulo: "Promedio de interacciones positivas por persona",
    grupo: GRUPO_INTENSIDAD,
    render: (m) => textoDePromedio(m.promedioInteraccionesPorPersona),
  },
  { titulo: "Sesiones", grupo: GRUPO_INTENSIDAD, render: (m) => textoDeEntero(m.sesiones) },
  {
    titulo: "Sesiones por persona",
    grupo: GRUPO_INTENSIDAD,
    render: (m) => textoDePromedio(m.sesionesPorPersona),
  },
  {
    titulo: "Mensajes de las personas",
    grupo: GRUPO_INTENSIDAD,
    render: (m) => textoDeEntero(m.mensajesDeLasPersonas),
  },
]

/** Las dos columnas de periodo y la de acciones, que no pertenecen a ningún grupo. */
export const COLUMNA_ITEM = "Item"
export const COLUMNA_INTERVALO = "Desde – Hasta"
export const COLUMNA_ACCIONES = "Acciones"

// ════════════════════════════════════════════════════════════════════════════
// «QUÉ SIGNIFICA CADA MÉTRICA» — LAS DIECISIETE ENTRADAS
// ════════════════════════════════════════════════════════════════════════════

export const TITULO_DEL_DICCIONARIO = "Qué significa cada métrica"

/** La entrada del diccionario de «Pidió humano», que el brazo sin derivación no tiene. */
export const ENTRADA_PIDIO_HUMANO = "Pidió humano:"

/**
 * Una entrada del diccionario: su rótulo (con los dos puntos, como el PRD lo
 * escribe) y su texto.
 */
export interface EntradaDelDiccionario {
  readonly entrada: string
  readonly texto: string
}

/**
 * **LAS DIECISIETE, EN EL ORDEN DEL PRD** (`PRD_R4.md:1688-1704`), de «Mensaje
 * inductor:» a «En el CSV:», que es lo que `R4-F3-CA-45` exige textualmente.
 *
 * ⚠️ El Brazo sin derivación tiene DIECISÉIS: se le saca `ENTRADA_PIDIO_HUMANO`
 * (`R4-F3-CA-47`). El filtro es por la misma capacidad que saca la columna, no
 * por un índice.
 */
export const DICCIONARIO: readonly EntradaDelDiccionario[] = [
  {
    entrada: "Mensaje inductor:",
    texto:
      "el mensaje con el que empieza cada envío (la plantilla que se manda desde Nuevo envío).",
  },
  {
    entrada: "Enviados:",
    texto:
      "mensajes inductores mandados dentro del periodo. Dos envíos a la misma persona cuentan 2.",
  },
  {
    entrada: "Entregados:",
    texto:
      "de esos mensajes inductores, cuántos llegaron según Meta. Porcentaje sobre Enviados.",
  },
  {
    entrada: "Leídos:",
    texto:
      "cuántos se leyeron según Meta. Porcentaje sobre Entregados. Es una métrica de referencia, no un dato duro: solo cuenta a quien tiene activada la confirmación de lectura.",
  },
  {
    entrada: "Interacción positiva:",
    texto:
      "personas que escribieron o tocaron cualquier botón dentro del periodo, menos el de darse de baja.",
  },
  {
    entrada: "2+ interacciones positivas:",
    texto:
      "personas con dos o más interacciones positivas dentro del periodo. Cada mensaje escrito o botón tocado cuenta 1.",
  },
  {
    entrada: "Interacción positiva vs. periodo anterior:",
    texto:
      "cuánto cambió el número de personas con interacción positiva respecto del periodo anterior.",
  },
  {
    entrada: "Sin interacción:",
    texto:
      "personas que recibieron un mensaje inductor y no escribieron ni tocaron ningún botón dentro del periodo. Quien solo tocó el botón de baja no está aquí: aparece en Opt-out. Quien responde recién en el periodo siguiente queda aquí en el periodo del mensaje inductor.",
  },
  {
    entrada: "Siguió con el chatbot:",
    texto:
      "personas que escribieron al menos un mensaje, o tocaron un botón que marca que siguió con el chatbot, dentro del periodo.",
  },
  {
    entrada: ENTRADA_PIDIO_HUMANO,
    texto:
      "personas que tocaron el botón para hablar con una persona dentro del periodo. Cuenta el toque, no si después hablaron con alguien.",
  },
  {
    entrada: "Opt-out:",
    texto:
      "personas que se dieron de baja dentro del periodo. Si después se revierte la baja, en este periodo sigue contando.",
  },
  {
    entrada: "Promedio de interacciones positivas por persona:",
    texto: "cuántas veces respondió, en promedio, cada persona con interacción positiva.",
  },
  {
    entrada: "Sesiones:",
    texto:
      "sesiones que empezaron dentro del periodo y en las que la persona escribió o tocó al menos un botón. Una sesión se corta después de 24 horas sin mensajes de la persona.",
  },
  {
    entrada: "Sesiones por persona:",
    texto: "Sesiones dividido por las personas que tuvieron al menos una.",
  },
  {
    entrada: "Mensajes de las personas:",
    texto:
      "todo lo que mandaron las personas dentro del periodo: textos, botones, audios, imágenes y demás.",
  },
  {
    entrada: "Periodo:",
    texto:
      "Desde a las 00:00 y Hasta a las 23:59, en hora de Chile. Una persona puede estar en varias columnas de «Personas», así que no suman 100 %.",
  },
  {
    entrada: "En el CSV:",
    texto:
      "cada número y su porcentaje van en columnas separadas, por ejemplo «Mensajes inductores entregados» y «Mensajes inductores entregados (%)». En la pantalla se muestran juntos en la misma celda.",
  },
]

/**
 * La nota del brazo CON derivación, debajo del diccionario (`PRD_R4.md:1706`,
 * `R4-F3-CA-46`). El brazo sin derivación no la tiene (`R4-F3-CA-47`).
 */
export const NOTA_DEL_BRAZO =
  "La columna Pidió humano aparece porque este brazo tiene derivación a humano."

// ════════════════════════════════════════════════════════════════════════════
// LA LISTA FIJA DE ERRORES DE META
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⛔ **EL RÓTULO DEL RESTO, ATADO AL CONTRATO SIN IMPORTAR UN VALOR.**
 *
 * `contracts/analytics/index.ts:1554` exporta `OTROS_ERRORES = "Otros errores"`.
 * `typeof import(...)` es una construcción de TIPO —`esbuild` la borra— así que
 * esto NO mete `contracts/` en el bundle y sigue la regla de `vite.config.ts`.
 * Es el mismo recurso con el que `borde-metricas.ts` ata sus rutas literales: el
 * día que el contrato le cambie el texto, **este archivo no compila** en vez de
 * dibujar un rótulo que el CSV ya no usa.
 */
type RotuloDelResto = typeof import("@contracts/analytics/index.ts").OTROS_ERRORES
export const OTROS_ERRORES: RotuloDelResto = "Otros errores"

/**
 * Una columna de la Tabla 2: el código que cuenta y el rótulo completo que se
 * dibuja.
 *
 * ⚠️ `codigo` es `null` SÓLO en «Otros errores», que no cuenta UN código sino
 * todos los que no están en la lista. Un `codigo: ""` o un centinela
 * (`"otros"`) se compararía contra los `code` que llegan del cable y podría
 * coincidir con un código real de Meta; `null` no puede.
 */
export interface ColumnaDeError {
  readonly codigo: string | null
  readonly rotulo: string
}

/**
 * **LAS SEIS COLUMNAS DE ERROR, EN EL ORDEN DEL PRD** (`PRD_R4.md:1718-1724`),
 * la misma lista en los dos brazos (`R4-F3-CA-40`, `R4-F3-CA-44`).
 *
 * ⛔ Los cinco códigos y su orden son los de
 * `contracts/analytics/index.ts:1545` (`ERRORES_DE_META_FIJOS`): `131026`,
 * `131049`, `131050`, `131047`, `130429`. **El orden NO es numérico** —`131047`
 * va cuarto, después de `131050`— así que ordenar la lista «para prolijear» la
 * rompe.
 *
 * ⚠️ `ERRORES_DE_META_FIJOS` está declarada `readonly string[]` y no como tupla
 * de literales, así que `typeof import(...)` no da un tipo que ate los cinco
 * valores uno por uno: lo que ata es el examen
 * `pruebas/r4-uim1-tablas.test.ts`, que importa la constante del contrato por
 * ruta relativa y compara los cinco códigos y su orden.
 *
 * ⛔ **La descripción de cada código es la del PRD, que es el motivo de ese
 * código en la tabla de motivos de Conversaciones.** `PRD_R4.md:1752` lo dice
 * para el CSV («su descripción, que es el motivo de ese código en la tabla de
 * motivos de Conversaciones»); el rótulo de la COLUMNA lo fija `R4-F3-CA-40`
 * entre comillas y es lo que se escribe.
 */
export const COLUMNAS_DE_ERROR: readonly ColumnaDeError[] = [
  { codigo: "131026", rotulo: "131026 · El teléfono no puede recibir mensajes de WhatsApp." },
  {
    codigo: "131049",
    rotulo:
      "131049 · Meta no entregó el mensaje para cuidar la experiencia del participante.",
  },
  {
    codigo: "131050",
    rotulo: "131050 · El participante pidió no recibir mensajes de marketing.",
  },
  {
    codigo: "131047",
    rotulo: "131047 · Pasaron más de 24 horas desde el último mensaje del participante.",
  },
  { codigo: "130429", rotulo: "130429 · Se superó el ritmo de envío permitido por Meta." },
  { codigo: null, rotulo: OTROS_ERRORES },
]

/** Los cinco códigos de la lista fija, para decidir qué cae en «Otros errores». */
const CODIGOS_FIJOS: readonly string[] = COLUMNAS_DE_ERROR.flatMap((columna) =>
  columna.codigo === null ? [] : [columna.codigo],
)

/**
 * El conteo de una columna de error en una fila del cable.
 *
 * `PRD_R4.md:1726`: *«Las celdas muestran el número, «0» cuando no hubo
 * ninguno»* — así que la ausencia de un código en `errors` es un CERO, no un
 * «—»: el borde manda los códigos que ocurrieron y no los que no.
 *
 * ⛔ **«Otros errores» SUMA, no cuenta uno.** `PRD_R4.md:1724`: *«Con cualquier
 * otro código»*, y `PRD_R4.md:1726`: *«"Otros errores" suma cualquier otro
 * código de Meta»*. La salida cómoda —buscar un `code` igual a «Otros errores»—
 * daría 0 siempre y en silencio: el cable manda códigos de Meta, no rótulos.
 *
 * ⛔ **Y no se recorta ni se redondea nada**: son conteos enteros del servidor.
 * Se pasan por la regla de B15 para el punto de miles y nada más.
 */
export function conteoDeError(
  errores: readonly { readonly code: string; readonly count: number }[],
  columna: ColumnaDeError,
): number {
  if (columna.codigo === null) {
    return errores
      .filter((error) => !CODIGOS_FIJOS.includes(error.code))
      .reduce((suma, error) => suma + error.count, 0)
  }
  return errores.find((error) => error.code === columna.codigo)?.count ?? 0
}

// ════════════════════════════════════════════════════════════════════════════
// LAS LEYENDAS Y LAS NOTAS AL PIE DE LAS DOS TABLAS
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⭐ **A QUIÉN NOMBRA LA LEYENDA CUANDO NO HAY BRAZO** — y es un verde que ya
 * existía, no una invención de esta etapa.
 *
 * ⛔⛔ `pruebas/r11-columnas-y-leyendas.test.tsx` §`R1.1-F1-CA-53` monta
 * `PantallaMetricas` **sin** `brazo` —la única forma de alcanzar esa rama, porque
 * `PRD_R1.1.md:204` dice que ninguna dirección puede producir hoy un Métricas sin
 * brazo— y afirma: *«Y sin brazo la leyenda no inventa uno»*,
 * `toContain("Resultados por periodo del estudio.")`.
 *
 * ⚠️ **Medido:** con el sello vacío interpolado a secas, la leyenda salía
 * *«Resultados por periodo de . «Mensajes inductores» …»* —con un punto suelto— y
 * ese caso se ponía **rojo**. No es un rojo de la etapa: es un comportamiento que
 * existía y que el PRD de R4 no contradice (`PRD_R4.md:1637` escribe la leyenda
 * «de KAI · Brazo X», o sea el caso CON brazo, y no dice nada del caso sin él).
 *
 * ⇒ **lo que existía se mantiene y lo nuevo se agrega:** sin sello la primera
 * frase es la de siempre; las DOS frases que R4 agrega van en los dos casos. Con
 * sello, el texto es exactamente el de `R4-F3-CA-09`, carácter por carácter.
 */
const SIN_BRAZO = "del estudio"

/**
 * La leyenda de la Tabla 1 (`PRD_R4.md:1637`, `R4-F3-CA-09`), con el sello del
 * brazo adentro.
 *
 * ⛔ Toma el SELLO ya armado (`selloDelBrazo()`) y no el identificador del
 * brazo: acá no se escribe «KAI» ni se compone el nombre de la instancia.
 */
export function leyendaDeResultados(sello: string): string {
  const aQuien = sello === "" ? SIN_BRAZO : `de ${sello}`
  return `Resultados por periodo ${aQuien}. «Mensajes inductores» cuenta mensajes; «Personas» cuenta personas, con su porcentaje sobre las personas que recibieron al menos un mensaje inductor en el periodo.`
}

/**
 * La leyenda de la Tabla 2 (`PRD_R4.md:1712`, `R4-F3-CA-39`).
 *
 * ⚠️ Misma red que la de arriba, por el mismo motivo: sin brazo no se interpola
 * un sello vacío. Acá no hay un caso heredado que lo afirme —la Tabla 2 de R4 es
 * nueva— pero el modo de falla es idéntico y dejarlo sin red sería dejarlo para
 * que lo encuentre una persona en pantalla.
 */
export function leyendaDeErrores(sello: string): string {
  const aQuien = sello === "" ? SIN_BRAZO : `de ${sello}`
  return `Errores de Meta en los mensajes inductores ${aQuien}, por periodo: cuántos mensajes inductores fallaron por cada error.`
}

/** La nota debajo de la Tabla 2 (`PRD_R4.md:1726`, `R4-F3-CA-43`). */
export const NOTA_DE_LOS_ERRORES =
  "Los dos brazos muestran la misma lista de errores. «Otros errores» suma cualquier otro código de Meta; el CSV de errores trae el código exacto de cada uno."
